// src/store/slices/passwordSlice.ts

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";

export interface HashResult {
    sha512Hash: string;
    keyHex: string;
    encoded: string;
    hash: Uint8Array;
  }

export interface PocAuthState {
    password: string;
    email: string;
    hashResult: HashResult | null;
    hashStatus: 'idle' | 'calculating' | 'ready' | 'error';
    errorMessage: string | null;
    derivedKey: string | null; // The Argon2-derived key (from password)
    kyberKeyPair: {
      publicKey: Uint8Array | null;
      privateKey: Uint8Array | null;
    };
    encapsulatedKey: {
      sharedSecret: Uint8Array | null;
      ciphertext: Uint8Array | null;
    } | null;
    privateKeyHash: string | null;
  }

// Initial state
const initialState: PocAuthState = {
  password: 'oreo',
  hashResult: null,
  hashStatus: 'idle',
  errorMessage: null,
  email: 'adhochstetter@gmail.com',
    kyberKeyPair: {
    publicKey: null,
    privateKey: null
  },
  encapsulatedKey: null,
  privateKeyHash: null,
  derivedKey: null
};

// Async thunk for calculating password hash
export const calculateHash = createAppAsyncThunk(
  'password/calculateHash',
  async(_,   { extra: { pocServices }, dispatch, getState, rejectWithValue }) => {
    const state = getState();

    const { pocAuth: { password, email } } = state;

    try {
      // First, create a SHA-512 hash of the combined input to use as email
        return await pocServices.cryptoService.hashPassword(password, email);
    } catch (err) {
      throw new Error(`Error generating hash: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
);

// Create the password slice
const pocAuthSlice = createSlice({
  name: 'pocAuth',
  initialState,
  reducers: {
    setPassword: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      state.password = payload

      const { hashResult } = state;

      // Reset hash when password changes
      if (hashResult !== null) {
        state.hashStatus = 'idle';
        state.hashResult = null;
      }
    },
    setEmail: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      state.email = payload;

      // Reset hash when email changes
      const { hashResult } = state;
      if (hashResult !== null) {
        state.hashStatus = 'idle';
        // state.hashResult = null;
      }
    },
    clearPassword: (state) => {
      state.password = '';
      state.hashResult = null;
      state.hashStatus = 'idle';
      state.errorMessage = null;
    },
    setDerivedKey: (state, action: PayloadAction<string>) => {
      state.derivedKey = action.payload;
    },
    clearKeys: (state) => {
      state.derivedKey = null;
      state.encapsulatedKey = null;
      state.kyberKeyPair = {
        publicKey: null,
        privateKey: null
      };
      state.privateKeyHash = null;
      state.errorMessage = null;
    },
    setKeyPair: (state, action: PayloadAction<{
      publicKey: Uint8Array;
      privateKey: Uint8Array;
    }>) => {
      state.kyberKeyPair = action.payload;
    },

    setPrivateKeyHash: (state, action: PayloadAction<string>) => {
      state.privateKeyHash = action.payload;
    },
    setHashResult: (state, action: PayloadAction<HashResult>) => {
      state.hashResult = action.payload;
    }

  },
  extraReducers: (builder) => {
    builder
      // Calculate hash cases
      .addCase(calculateHash.pending, (state) => {
        state.hashStatus = 'calculating';
        state.errorMessage = null;
      })
      .addCase(calculateHash.fulfilled, (state, action) => {
        const { payload } = action;
        state.hashResult = payload;
        state.hashStatus = 'ready';
      })
      .addCase(calculateHash.rejected, (state, { error: { message } }) => {
        state.hashStatus = 'error';
        state.errorMessage = message ?? 'Unknown error';
      });
  },
});

export const { actions: { setPassword, setEmail, clearPassword, setDerivedKey, clearKeys, setKeyPair, setPrivateKeyHash } } = pocAuthSlice;

export default pocAuthSlice.reducer;
